import React, { useMemo } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { resolveImageEntries } from '../../utils/entityImages';
import { isPermanentSpawn } from '../../utils/npcLocation';
import { npcs } from '../../data/guide/npcs';
import type { Monster, WorldData } from '../../types/world';
type Row = {
  m: Monster;
  maps: string[];
};
type Group = {
  key: string;
  title: string;
  note: string;
  rows: Row[];
};

/**
 * Lista de monstruos del catálogo (06_monsters) que todavía no tienen foto, para conseguirlas.
 * Agrupados por prioridad: invasiones/eventos, monstruos con aparición permanente, posibles NPC
 * (nivel 0 o sin nivel) y entradas sin ninguna aparición en los archivos.
 */
export function MissingMonsterPhotos({
  w
}: {
  w: WorldData;
}) {
  const groups = useMemo<Group[]>(() => {
    const {
      byMonster
    } = resolveImageEntries(w);
    const guideNpcIds = new Set(npcs.map(n => n.monsterId).filter((x): x is number => x !== undefined));
    const mapName = (id: number) => w.maps.get(id)?.name ?? `Mapa ${id}`;
    const perm = new Map<number, Set<string>>();
    const temp = new Map<number, Set<string>>();
    w.spawns.forEach(s => {
      const target = isPermanentSpawn(s) ? perm : temp;
      const set = target.get(s.monsterId) ?? new Set<string>();
      set.add(mapName(s.mapId));
      target.set(s.monsterId, set);
    });
    const invasion: Row[] = [];
    const permanent: Row[] = [];
    const npcLike: Row[] = [];
    const noSpawn: Row[] = [];
    w.monsters.forEach(m => {
      if (byMonster.has(m.id) || guideNpcIds.has(m.id)) return;
      const p = perm.get(m.id);
      const t = temp.get(m.id);
      const row: Row = {
        m,
        maps: Array.from(p ?? t ?? [])
      };
      if (!m.level) npcLike.push(row);else if (t && !p) invasion.push(row);else if (p) permanent.push(row);else noSpawn.push(row);
    });
    const byLevel = (a: Row, b: Row) => (a.m.level ?? 0) - (b.m.level ?? 0) || a.m.id - b.m.id;
    return [{
      key: 'inv',
      title: 'Invasiones y eventos',
      note: 'Solo aparecen en invasiones o eventos. Prioridad: los de las invasiones publicadas.',
      rows: invasion.sort(byLevel)
    }, {
      key: 'perm',
      title: 'Monstruos de mapa',
      note: 'Tienen aparición permanente en algún mapa. Ordenados por nivel.',
      rows: permanent.sort(byLevel)
    }, {
      key: 'npc',
      title: 'Nivel 0 o sin nivel en el catálogo',
      note: 'Identidad por revisar (pueden ser NPC, objetos de evento o monstruos). El nivel no decide su clasificación.',
      rows: npcLike.sort((a, b) => a.m.id - b.m.id)
    }, {
      key: 'none',
      title: 'Sin aparición en los archivos',
      note: 'Están en el catálogo pero no en 08_spawns. Menor prioridad.',
      rows: noSpawn.sort(byLevel)
    }];
  }, [w]);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const text = [`Monstruos sin foto: ${total}. Nombra cada archivo monstruo_ID (p. ej. monstruo_${groups[0].rows[0]?.m.id ?? 14}).`, ...groups.flatMap(g => ['', `${g.title} (${g.rows.length})`, ...g.rows.map(r => `monstruo_${r.m.id} — ${r.m.name}${r.m.level ? ` · nivel ${r.m.level}` : ''}${r.maps.length ? ` · ${r.maps.slice(0, 3).join(', ')}${r.maps.length > 3 ? '…' : ''}` : ''}`)])].join('\n');
  return <div className="mb-6" data-id="element-1488" data-mp="ep6hzlkq5u2p">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3" data-id="element-1489" data-mp="enve7lra4mbl">
        <h3 className="text-base font-semibold text-white" data-id="element-1490" data-mp="jmy9iehgje6d">Monstruos sin foto <span className="font-normal text-gray-400" data-id="element-1491" data-mp="5nyjb2eaqr83">· {total}</span></h3>
        <CopyTextButton text={text} label="Copiar lista de monstruos sin foto" data-id="element-1492" data-mp="5mngp5wl7pik" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2" data-id="element-1493" data-mp="aoctor3ded9a">
        {groups.map(g => <details key={g.key} className="rounded-2xl border border-white/10 p-4" open={g.key === 'inv'} data-id="element-1494" data-mp="3rwgksocidw84">
            <summary className="cursor-pointer text-sm font-semibold text-white" data-id="element-1495" data-mp="pdudz4hvtyy">
              {g.title} <span className="font-normal text-gray-400" data-id="element-1496" data-mp="1q53go1id7sd4">· {g.rows.length}</span>
            </summary>
            <p className="mt-1 text-xs text-gray-500" data-id="element-1497" data-mp="1v1lh4p6nx4jn">{g.note}</p>
            <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto pr-1 text-sm" data-id="element-1498" data-mp="3r6jhxrvrsj5d">
              {g.rows.map(r => <li key={r.m.id} className="flex items-baseline justify-between gap-3" data-id="element-1499" data-mp="3n5hib9xxy52v">
                  <span className="min-w-0 truncate text-gray-200" data-id="element-1500" data-mp="3i4k04xcxlb1d">
                    <span className="font-mono text-xs text-gray-500" data-id="element-1501" data-mp="3d68qammmz0j5">monstruo_{r.m.id}</span> {r.m.name}
                  </span>
                  <span className="shrink-0 text-xs text-gray-500" data-id="element-1502" data-mp="387f9olc1yter">{r.m.level ? `Nv ${r.m.level}` : ''}{r.maps[0] ? ` · ${r.maps[0]}` : ''}</span>
                </li>)}
              {!g.rows.length && <li className="text-gray-500" data-id="element-1503" data-mp="2ze27stw0ghgj">Ninguno.</li>}
            </ul>
          </details>)}
      </div>
    </div>;
}